/**
 * Registry Bulk Birthday/Anniversary Import — Feb 2026. Founder-only.
 * Upload workflow: pick file -> preview (match against existing employees
 * by email, update-only, no new people ever created) -> commit -> summary.
 * CSV + XLSX supported. Mirrors RegistryBulkUploadDialog's flow.
 */
import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/Layout";
import { CakeSlice, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

export default function RegistryBirthdayImportDialog({ onClose, onDone }) {
  const [step, setStep] = useState("pick"); // pick | preview | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { rows, summary, header_errors }
  const [headerErrors, setHeaderErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const downloadTemplate = (format) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/registry/employees/bulk-birthdays-template?format=${format}`;
    window.open(url, "_blank");
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setHeaderErrors([]); }
  };

  const runPreview = async () => {
    if (!file) { toast.error("Choose a CSV or Excel file first"); return; }
    setBusy(true);
    setHeaderErrors([]);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/registry/employees/bulk-birthdays-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.header_errors?.length) {
        setHeaderErrors(data.header_errors);
        return;
      }
      setPreview(data);
      setStep("preview");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const runCommit = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/registry/employees/bulk-birthdays-commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data.summary);
      setStep("done");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const close = () => { onClose(); };
  const matchedCount = preview?.summary?.matched || 0;

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-sm w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto grid-cols-1" data-testid="registry-birthday-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display inline-flex items-center gap-2"><CakeSlice size={16} className="text-[#F54800]" /> Bulk import — Birthdays &amp; Anniversaries</DialogTitle>
          <DialogDescription className="text-xs">
            Import dates of birth and work-anniversary dates for existing employees from a CSV or Excel file. This never creates new people — every row must match an employee by email.
          </DialogDescription>
        </DialogHeader>

        {step === "pick" && (
          <div className="space-y-4">
            {headerErrors.length > 0 && (
              <div className="bg-red-50 border border-red-300 text-red-800 text-xs rounded-sm px-3 py-2" data-testid="registry-birthday-header-errors">
                {headerErrors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => downloadTemplate("xlsx")} data-testid="registry-birthday-template-xlsx">
                <Download size={12} className="mr-1.5" /> Download template (.xlsx)
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => downloadTemplate("csv")} data-testid="registry-birthday-template-csv">
                <Download size={12} className="mr-1.5" /> Download template (.csv)
              </Button>
            </div>
            <div
              onClick={() => inputRef.current?.click()}
              className="border-2 border-dashed border-slate-200 hover:border-[#F54800] rounded-sm p-8 text-center cursor-pointer transition-colors"
              data-testid="registry-birthday-dropzone"
            >
              <FileSpreadsheet size={28} className="mx-auto text-slate-300 mb-2" />
              {file ? (
                <div className="text-sm font-medium text-slate-800">{file.name}</div>
              ) : (
                <div className="text-sm text-slate-500">Click to choose a .csv or .xlsx file</div>
              )}
              <input ref={inputRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={onPickFile} data-testid="registry-birthday-file-input" />
            </div>
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-3">
            <div className="flex gap-2 text-xs">
              <Tag tone="slate">{preview.summary.total} row{preview.summary.total === 1 ? "" : "s"}</Tag>
              <Tag tone="green">{matchedCount} will update</Tag>
              {preview.summary.invalid > 0 && <Tag tone="red">{preview.summary.invalid} invalid</Tag>}
            </div>
            <div className="max-h-[45vh] overflow-y-auto border border-slate-200 rounded-sm">
              <table className="w-full text-xs" data-testid="registry-birthday-preview-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    {["Row", "Employee Email", "Date of Birth", "Anniversary Date", "Status"].map((h) => (
                      <th key={h} className="label-overline px-2 py-2 text-left whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.rows.map((r) => (
                    <tr key={r.row} className={r.errors.length ? "bg-red-50" : "bg-green-50"} data-testid={`registry-birthday-row-${r.row}`}>
                      <td className="px-2 py-2">{r.row}</td>
                      <td className="px-2 py-2">{r.data.employee_email || "—"}</td>
                      <td className="px-2 py-2">{r.data.date_of_birth || "—"}</td>
                      <td className="px-2 py-2">{r.data.joining_date || "—"}</td>
                      <td className="px-2 py-2">
                        {r.errors.length ? (
                          <span className="text-red-700 inline-flex items-center gap-1" title={r.errors.join("; ")}><AlertTriangle size={11} /> {r.errors[0]}</span>
                        ) : (
                          <span className="text-green-700">matches {r.existing_name}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="text-center py-6" data-testid="registry-birthday-success-summary">
            <CheckCircle2 size={32} className="mx-auto text-green-600 mb-3" />
            <div className="font-display text-base font-bold text-slate-900">
              {result.updated} Employee{result.updated === 1 ? "" : "s"} Updated
              {result.failed > 0 && `, ${result.failed} Failed`}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "pick" && (
            <>
              <Button variant="outline" className="rounded-sm" onClick={close}>Cancel</Button>
              <Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={runPreview} disabled={!file || busy} data-testid="registry-birthday-preview-btn">
                {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null} Preview
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => setStep("pick")}>Back</Button>
              <Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={runCommit} disabled={busy || matchedCount === 0} data-testid="registry-birthday-commit-btn">
                {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null} Import {matchedCount} update{matchedCount === 1 ? "" : "s"}
              </Button>
            </>
          )}
          {step === "done" && (
            <Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={close} data-testid="registry-birthday-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
